import { Icon } from '@/app/icons'
import { cn } from '@/shared/ui/primitives'
import { formatDateTime, relativeTime } from '@/shared/format'
import { EVENT_TONE, type NotificationEntry } from './api'

/**
 * One line in the bell, and the same line on the full page.
 *
 * <h2>Unread is shown twice, on purpose</h2>
 * A coloured dot and a heavier title. Colour alone would exclude anyone who
 * cannot distinguish it, and in a list where every row looks similar the
 * weight is what the eye actually catches.
 *
 * <h2>Two layouts, one row</h2>
 * The bell hangs a narrow panel off the top bar, so the time sits under the
 * text. On the full page the row has the width of a table, and reads like one:
 * the time in its own column on the right, where the eye runs down it.
 */
export function NotificationItem({
  entry,
  onSelect,
  layout = 'menu',
}: {
  entry: NotificationEntry
  onSelect: (entry: NotificationEntry) => void
  layout?: 'menu' | 'page'
}) {
  const page = layout === 'page'
  // Relative for reading, absolute on hover for the person who has to write
  // the time down in a ticket.
  const time = (
    <span
      className={cn('text-xs text-gray-400', page ? 'whitespace-nowrap tabular-nums sm:text-gray-500' : 'mt-1 block')}
      title={formatDateTime(entry.createdAt)}
    >
      {relativeTime(entry.createdAt)}
    </span>
  )

  return (
    <button
      type="button"
      onClick={() => onSelect(entry)}
      className={cn(
        'flex w-full items-start gap-3 text-left transition-colors hover:bg-gray-100',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary',
        page ? 'px-5 py-3.5' : 'px-4 py-3',
        // No alpha on a theme colour: where color-mix is unsupported it falls
        // back to the solid value, and #f1faff is already the pale tint this
        // wants.
        !entry.read && 'bg-primary-light',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          entry.read ? 'bg-transparent ring-1 ring-gray-300' : EVENT_TONE[entry.eventType],
        )}
      />

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate',
            entry.read ? 'text-gray-700' : 'font-semibold text-gray-900',
          )}
        >
          {entry.title}
        </span>
        {entry.summary && (
          <span className={cn('mt-0.5 block line-clamp-2 text-gray-500', page ? 'text-xs' : 'text-sm')}>
            {entry.summary}
          </span>
        )}
        {/* On a phone the page row is as narrow as the bell, so the time goes
            back under the text there too. */}
        {page ? <span className="mt-1 block sm:hidden">{time}</span> : time}
      </span>

      {page && (
        <span className="hidden shrink-0 items-center gap-3 pt-0.5 sm:flex">
          {time}
          <Icon
            name="arrowRight"
            className={cn('size-4', entry.linkPath ? 'text-gray-400' : 'invisible')}
          />
        </span>
      )}

      {!entry.read && <span className="sr-only">Belum dibaca</span>}
    </button>
  )
}
